
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { NavLink } from "react-router-dom";

import { faCircleExclamation,  faXmark } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import SidebarModulesContainer from "../../pages/Dashboards/Components/SidebarModulesContainer";
import { AppDispatch, AppState } from "../../store/configureStore";
import { getSidebar, setIsSidebarOpen } from "../../store/slices/sidebar";
import { logoutRequested } from "../../store/users/auth";
import getSidebarItems from "./sidebarItems";

import Task from "entities/Task";
import { TaskStatus } from "entities/TaskStatus";
import { roles } from "../../util/config/roles";
import Avatar from "../Avatar/Avatar";
import CompanyLogo from "../CompanyLogo/CompanyLogo";
import LocaleSelect from "../Locale/LocaleSelect";

const Sidebar = () => {
    const [sidebarVariant, setSidebarVariant] = useState(false);

    const { isSuperAdmin, isSuperAdminOrAdmin } = roles();

    const isLoggedIn = useSelector((state: AppState) => state.users.auth.isLoggedIn);
    const { data: contract } = useSelector(
        (state: AppState) => state.entities.contracts.selectedContract
    );
    const { itemIds } = useSelector((state: AppState) => state.entities.sidebar);

    const { t } = useTranslation("common");

    //22 is landingPage and has to be always visible
    const allowedItemIds = [...itemIds, "22"];
    const sidebarItems = useMemo(
        () => getSidebarItems(allowedItemIds, contract?.IsManager),
        [allowedItemIds]
    );

    const dispatch = useDispatch<AppDispatch>();

    useEffect(() => {
        if (!isLoggedIn || isSuperAdmin) return;
        dispatch(getSidebar());

    }, [isLoggedIn, isSuperAdmin, dispatch]);

    const { sidebarIsOpen } = useSelector((state: AppState) => state.entities.sidebar);

    const closeSidebar = () => {
        dispatch(setIsSidebarOpen(false));
    };

    const setSidebarSize = () => {
        setSidebarVariant(!sidebarVariant);
    };

    // Handle clicking outside the sidebar
    useEffect(() => {
        const handleOutsideClick = (event: MouseEvent) => {
            const target = event.target as Element;

            if (sidebarIsOpen &&
                !target.closest(".s_sidebar") &&
                !target.closest(".user-menu")) {
                closeSidebar();
            }
        };


        if (sidebarIsOpen) {
            document.addEventListener("click", handleOutsideClick);
        }

        return () => {
            document.removeEventListener("click", handleOutsideClick);
        };
    }, [sidebarIsOpen]);

    const { list: tasks } = useSelector(
        (state: AppState) => state.entities.tasks.tasks
    );

    const hasUnCompletedItems = (rows: Task[]): boolean => {
        const items = rows.filter(item => item.status === TaskStatus.Open);

        return items.length !== 0;
    };

    const hasUncompleted = hasUnCompletedItems([...tasks]);

    const logout = (e: React.MouseEvent) => {
        e.preventDefault();
        dispatch({ type: logoutRequested.type });
    };

    return (
        <>
            {/* Sidebar */}
            <div
                className={`s_sidebar ${sidebarIsOpen ? "s_sidebar--show" : ''} ${sidebarVariant ? "s_sidebar--variant_size" : ''}`}
            >
                <CompanyLogo className="mb-3 mt-1" />

                <div className="user-menu">
                    <Avatar />
                    <LocaleSelect />
                    <div className="s_close" onClick={closeSidebar}>
                        <FontAwesomeIcon icon={faXmark} />
                    </div>
                </div>
                {isLoggedIn && (
                    <>
                        <div className="logged-in show-mobile-flex">
                            {/* <NavLink className="s_nav__item" to="/Password">
                                <div>{t("navmenu.changePassword")}</div>
                            </NavLink> */}

                            <NavLink className="s_nav__item" to="/" onClick={logout}>
                                <div>{t("navmenu.logout")}</div>
                            </NavLink>
                            <hr className="mb-4 mt-0" />
                        </div>
                    </>
                )}

                <nav className="s_nav">
                    {sidebarItems.map(p => (
                        <NavLink
                            key={p.to}
                            to={p.to}
                            className={({ isActive }) =>
                                `s_nav__item ${sidebarVariant ? "s_nav__item--variant_size" : ""} ${isActive ? "selected" : ""}`
                            }
                            onClick={closeSidebar}
                        >
                            <>
                                {` ${p.text}`}
                                {hasUncompleted && p.itemId === "9" &&
                                    <FontAwesomeIcon icon={faCircleExclamation} className="status-red unread-icon" />
                                }
                            </>
                        </NavLink>
                    ))}

                    {!isSuperAdminOrAdmin && (
                        <NavLink
                            to="/test-page"
                            className={({ isActive }) =>
                                `s_nav__item ${isActive ? "selected" : ""}`
                            }
                        >
                        Test Page
                    </NavLink>)}
                </nav>
            </div>
        </>
    );
};

export default Sidebar;
